/**
 * Loads AI Studio home tiles filtered by navigation screen READ permissions.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/state/auth/AuthProvider';
import { unifiedRBACService } from '@/services/unifiedRBACService';
import {
  AI_STUDIO_HOME_TILE_GROUPS,
  type AIStudioHomeTileDef,
  type AIStudioHomeTileGroupDef,
} from './homeTileConfig';

export interface AIStudioHomeTileGroup extends AIStudioHomeTileGroupDef {
  tiles: AIStudioHomeTileDef[];
}

async function filterTilesForUser(
  userId: string,
  groups: AIStudioHomeTileGroupDef[]
): Promise<AIStudioHomeTileGroup[]> {
  const permissionCache = new Map<string, boolean>();

  const hasRead = async (screenCode: string): Promise<boolean> => {
    if (permissionCache.has(screenCode)) {
      return permissionCache.get(screenCode) ?? false;
    }
    try {
      const allowed = await unifiedRBACService.checkScreenPermissionByCode(userId, screenCode, 'READ');
      permissionCache.set(screenCode, allowed);
      return allowed;
    } catch {
      permissionCache.set(screenCode, false);
      return false;
    }
  };

  const filteredGroups: AIStudioHomeTileGroup[] = [];

  for (const group of groups) {
    const visibleTiles: AIStudioHomeTileDef[] = [];
    for (const tile of group.tiles) {
      if (await hasRead(tile.screenCode)) {
        visibleTiles.push(tile);
      }
    }
    if (visibleTiles.length > 0) {
      filteredGroups.push({ ...group, tiles: visibleTiles });
    }
  }

  return filteredGroups;
}

export function useAIStudioHomeTiles() {
  const { user } = useAuth();
  const [groups, setGroups] = useState<AIStudioHomeTileGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!user?.id) {
        if (!cancelled) {
          setGroups([]);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const visible = await filterTilesForUser(user.id, AI_STUDIO_HOME_TILE_GROUPS);
        if (!cancelled) setGroups(visible);
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return { groups, loading };
}
